import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
} from "@nexus/ui"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { startLegacyHubMigration } from "@/features/opportunities/api/legacy-hub-migration.api"
import {
  legacyHubMigrationKeys,
  legacyHubMigrationPreviewQueryOptions,
  legacyHubMigrationStatusQueryOptions,
  taskProgressKeys,
} from "@/lib/queries"
import { LegacyHubMigrationDialog } from "../LegacyHubMigrationDialog/LegacyHubMigrationDialog"

export interface LegacyHubMigrationMenuProps {
  tenantId: string
}

/**
 * "…" overflow menu on the legacy opportunities page with the platform-admin "Migrate to hub" action
 * (AD-3547). The caller renders it only for super-tenant admins; the API enforces the same.
 */
export function LegacyHubMigrationMenu({ tenantId }: LegacyHubMigrationMenuProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [hasStarted, setHasStarted] = React.useState(false)
  const tenantName = useAuth((s) => s.tenants.find((tenant) => tenant.id === tenantId)?.name)

  const statusQuery = useQuery(legacyHubMigrationStatusQueryOptions(tenantId, true))
  const isRunning = statusQuery.data?.running ?? false
  const previewQuery = useQuery(
    legacyHubMigrationPreviewQueryOptions(tenantId, dialogOpen && !isRunning && !hasStarted),
  )

  const start = useMutation({
    mutationFn: startLegacyHubMigration,
    onSuccess: async () => {
      setHasStarted(true)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: legacyHubMigrationKeys.status(tenantId) }),
        queryClient.invalidateQueries({ queryKey: taskProgressKeys.all }),
      ])
    },
  })

  useMigrationFinishedToast(statusQuery.data?.running, statusQuery.data?.result?.status)

  const handleOpenChange = (open: boolean) => {
    setDialogOpen(open)
    if (!open) setHasStarted(false)
    if (open) void statusQuery.refetch()
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            className="min-h-11 min-w-11 text-muted-foreground hover:text-foreground [@media(hover:hover)_and_(pointer:fine)]:min-h-7 [@media(hover:hover)_and_(pointer:fine)]:min-w-7"
            aria-label={t("opportunities.legacyHubMigration.menuAria", "Admin actions")}
          >
            <Icon name="more-horizontal" size="sm" color="inherit" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {/* Stays clickable during a run: the dialog then shows its progress. */}
          <DropdownMenuItem
            {...(isRunning ? {} : { icon: "arrow-right" as const })}
            onClick={() => handleOpenChange(true)}
            className="cursor-pointer"
          >
            {isRunning && <Icon name="loader" size="xs" color="inherit" className="mr-2 animate-spin" />}
            {isRunning
              ? t("opportunities.legacyHubMigration.menuRunning", "Migrating to hub…")
              : t("opportunities.legacyHubMigration.menuItem", "Migrate to hub")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <LegacyHubMigrationDialog
        tenantId={tenantId}
        tenantName={tenantName}
        open={dialogOpen}
        onOpenChange={handleOpenChange}
        preview={previewQuery.data}
        // The cached dry run can predate a run that has since finished, so a refetch counts as loading:
        // the dialog never offers Migrate on counts that may already be out of date.
        isPreviewLoading={previewQuery.isFetching}
        isPreviewError={previewQuery.isError}
        status={statusQuery.data}
        hasStarted={hasStarted}
        isStarting={start.isPending}
        onConfirm={() => start.mutate()}
      />
    </>
  )
}

/** Toast once when a run this page watched goes from running to finished or failed. */
function useMigrationFinishedToast(running: boolean | undefined, resultStatus: string | undefined) {
  const { t } = useTranslation()
  const wasRunning = React.useRef(false)

  React.useEffect(() => {
    if (running) {
      wasRunning.current = true
      return
    }
    if (!wasRunning.current || running === undefined) return
    wasRunning.current = false
    if (resultStatus === "COMPLETED") {
      toast.success(t("opportunities.legacyHubMigration.toastCompleted", "Migration to hub complete"))
    } else if (resultStatus === "FAILED") {
      toast.error(t("opportunities.legacyHubMigration.toastFailed", "Migration to hub failed. It is safe to run it again."))
    }
  }, [running, resultStatus, t])
}
